import axiosInstance from "./AxiosInstance"
import { cachedRequest, clearCache } from "./requestCache"

const API_URL = "/mcqs"
const MCQS_PAGE_CAP = 100
const MCQS_DEFAULT_LIMIT = 10

export const getMcqs = async (limit = 10, page = 1, search = "", includeTotal = true) => {
  const safeLimit = Math.max(1, Math.min(MCQS_PAGE_CAP, Number.parseInt(limit, 10) || MCQS_DEFAULT_LIMIT))
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1)
  const safeSearch = search || ""
  return cachedRequest(`mcqs:list:${safeLimit}:${safePage}:${safeSearch}:${includeTotal ? "1" : "0"}`, 30000, async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}&includeTotal=${includeTotal ? "true" : "false"}`)
      return response.data
    } catch (error) {
      console.error("Error fetching MCQs", error)
      throw error
    }
  })
}

// Get MCQs by course ID
export const getMcqsByCourse = async (courseId, limit = 10, page = 1, search = "", trainerId) => {
  try {
    const qp = new URLSearchParams()
    qp.set("limit", String(limit))
    qp.set("page", String(page))
    if (search) qp.set("search", String(search))
    if (trainerId) qp.set("trainerId", String(trainerId))

    return await cachedRequest(`mcqs:byCourse:${courseId}:${limit}:${page}:${search}:${trainerId || ""}`, 30000, async () => {
      const response = await axiosInstance.get(`${API_URL}/course/${courseId}?${qp.toString()}`)
      return response.data
    })
  } catch (error) {
    console.error("Error fetching MCQs by course", error)
    throw error
  }
}

export const getMcqCourseWiseStats = async (params = {}) => {
  const qp = new URLSearchParams()
  const p = typeof params === "string" ? { trainerId: params } : (params || {})
  if (p.trainerId) qp.set("trainerId", String(p.trainerId))
  if (p.search) qp.set("search", String(p.search))
  if (p.page) qp.set("page", String(p.page))
  if (p.limit) qp.set("limit", String(p.limit))
  if (typeof p.includeTotal !== "undefined") qp.set("includeTotal", p.includeTotal ? "true" : "false")

  const key = `mcqs:courseWiseStats:${p.trainerId || "all"}:${p.search || ""}:${p.page || 1}:${p.limit || 10}:${p.includeTotal === false ? "0" : "1"}`
  return cachedRequest(key, 30000, async () => {
    const response = await axiosInstance.get(`${API_URL}/course-wise-stats?${qp.toString()}`)
    return response.data
  })
}

// Get single MCQ by ID
export const getMcqById = async (mcqId) => {
  try {
    return await cachedRequest(`mcqs:byId:${mcqId}`, 30000, async () => {
      const response = await axiosInstance.get(`${API_URL}/${mcqId}`)
      return response.data
    })
  } catch (error) {
    console.error("Error fetching MCQ by ID", error)
    try {
      const allMcqs = await getMcqs(100)
      const foundMcq = allMcqs.data.find((mcq) => mcq.id == mcqId)
      if (foundMcq) return { data: foundMcq }
      throw new Error("MCQ not found")
    } catch (fallbackError) {
      console.error("Fallback error:", fallbackError)
      throw error
    }
  }
}

// Add new MCQ
export const addMcq = async (mcqData) => {
  try {
    const response = await axiosInstance.post(API_URL,mcqData)
    clearCache("mcqs:")
    return response.data
  } catch (error) {
    console.error("Error adding MCQ", error)
    throw error
  }
}

// Update existing MCQ
export const updateMcq = async (mcq, mcqId) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${mcqId}`, mcq)
    clearCache("mcqs:")
    return response.data
  } catch (error) {
    console.error("Error updating MCQ", error)
    throw error
  }
}

// Delete MCQ
export const deleteMcq = async (mcqId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${mcqId}`)
    clearCache("mcqs:")
    return response.data
  } catch (error) {
    console.error("Error deleting MCQ", error)
    throw error
  }
}

// Bulk add MCQs
export const addMultipleMcqs = async (mcqsData) => {
  try {
    const promises = mcqsData.map((mcq) => addMcq(mcq))
    const results = await Promise.all(promises)
    return {
      data: results.map((result) => result.data),
      count: results.length,
    }
  } catch (error) {
    console.error("Error adding multiple MCQs", error)
    throw error
  }
}

// Get MCQ statistics by course
export const getMcqStatsByCourse = async (courseId) => {
  try {
    const mcqs = await getMcqsByCourse(courseId)
    return {
      totalMcqs: mcqs.data.length,
      courseId: courseId,
      lastUpdated: new Date().toISOString(),
    }
  } catch (error) {
    console.error("Error fetching MCQ stats", error)
    throw error
  }
}

// Search MCQs by question text
export const searchMcqs = async (searchTerm, courseId = null) => {
  try {
    let mcqs
    if (courseId) {
      mcqs = await getMcqsByCourse(courseId)
    } else {
      mcqs = await getMcqs(100)
    }

    const filteredMcqs = mcqs.data.filter(
      (mcq) =>
        mcq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mcq.option1.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mcq.option2.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mcq.option3.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mcq.option4.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    return {
      data: filteredMcqs,
      totalCount: filteredMcqs.length,
    }
  } catch (error) {
    console.error("Error searching MCQs", error)
    throw error
  }
}

// Validate MCQ data before submission
export const validateMcqData = (mcqData) => {
  const errors = []

  if (!mcqData.question || mcqData.question.trim().length < 10) {
    errors.push("Question must be at least 10 characters long")
  }

  if (!mcqData.option1 || !mcqData.option2 || !mcqData.option3 || !mcqData.option4) {
    errors.push("All four options are required")
  }

  if (!mcqData.answer) {
    errors.push("Correct answer is required")
  }

  const options = [mcqData.option1, mcqData.option2, mcqData.option3, mcqData.option4]
  if (!options.includes(mcqData.answer)) {
    errors.push("Answer must match one of the provided options")
  }

  if (!mcqData.courseId) {
    errors.push("Course ID is required")
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  }
}

export default {
  getMcqs,
  getMcqsByCourse,
  getMcqCourseWiseStats,
  getMcqById,
  addMcq,
  updateMcq,
  deleteMcq,
  addMultipleMcqs,
  getMcqStatsByCourse,
  searchMcqs,
  validateMcqData,
}
